import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Firestore, collection, getDocs, doc, getDoc } from '@angular/fire/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { Observable, from } from 'rxjs';
import { emailKey, signedInEmail } from '../helpers/signed-in-email';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private firestore: Firestore
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return from(this.checkAccess(state.url));
  }

  private checkAccess(returnUrl: string): Promise<boolean> {
    const auth = getAuth();

    return new Promise((resolve) => {
      onAuthStateChanged(auth, async (user) => {
        if (!user) {
          this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
          resolve(false);
          return;
        }

        // Layer 0: platform super-admin via Firebase Auth custom claim.
        // Set by the claimRoles Cloud Function for emails on the
        // SUPER_ADMIN_EMAILS allowlist (functions/superAdmins.js).
        // These accounts bypass every per-event check across the
        // platform — they're HEPCon staff scoped to the entire
        // product, not LOC organizers scoped to one event.
        //
        // forceRefresh=false reads the cached ID token (~1h TTL).
        // If a freshly-promoted user doesn't see the claim yet, ask
        // them to sign out + back in to force a token refresh.
        try {
          const tokenResult = await user.getIdTokenResult();
          if (tokenResult.claims['admin'] === true) {
            resolve(true);
            return;
          }
        } catch {
          // fall through to per-event permission check
        }

        // Record email, else the token's email claim: a custom-token account (email-code
        // login, preview hand-off) whose address is owned by an older password account has
        // no record email — see core/helpers/signed-in-email.ts.
        const currentEmail = await signedInEmail(user);
        if (!currentEmail) {
          // Anonymous-only or federated provider that didn't release
          // email — can't match against per-event permission docs.
          // Layer 0 already failed; deny.
          this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
          resolve(false);
          return;
        }
        const md5CurrentEmail = emailKey(currentEmail);

        // Layer 1: per-event admin OR moderator (convener) grant.
        // Admits organizers scoped to an event so conveners can moderate — not
        // only admins. Two sources per event, both checked:
        //   • events_management/{eventId}/permissions/{md5(email)}.role — set by
        //     the WebAdmin "Admins & Moderators" page (role 'admin' | 'moderator').
        //   • events/{eventId}/grants/{uid}.role / isModerator — set by the
        //     claimRoles CF (Indico convener-match ⇒ moderator), keyed by uid.
        // Single-doc reads (grants are public-read), so no collection-group rule
        // issue. Per-event data access is separately CF/rules-enforced, so a
        // permissive door here is safe. NOTE: events with no events_management
        // doc aren't scanned — a follow-up if a convener-only event needs it.
        const ADMITTED = ['admin', 'moderator'];
        const eventAdminsSnapshot = await getDocs(collection(this.firestore, 'events_management'));

        for (const eventAdminDoc of eventAdminsSnapshot.docs) {
          const eventId = eventAdminDoc.id;

          const permissionsRef = doc(this.firestore, `events_management/${eventId}/permissions/${md5CurrentEmail}`);
          const permissionsSnap = await getDoc(permissionsRef);
          if (permissionsSnap.exists() && ADMITTED.includes(permissionsSnap.data()['role'])) {
            resolve(true);
            return;
          }

          const grantRef = doc(this.firestore, `events/${eventId}/grants/${user.uid}`);
          const grantSnap = await getDoc(grantRef);
          if (grantSnap.exists()) {
            const g = grantSnap.data();
            if (ADMITTED.includes(g['role']) || g['isModerator'] === true) {
              resolve(true);
              return;
            }
          }
        }

        this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
        resolve(false);
      });
    });
  }
}
